/**
 * Host Global - Property Owner Closure Requests
 * Property owners can request to close their properties for certain days
 */

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Chip from "@mui/material/Chip";
import Alert from "@mui/material/Alert";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import CircularProgress from "@mui/material/CircularProgress";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";

// Components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Layout components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import PropertyOwnerFooter from "examples/PropertyOwnerFooter";

// API
import { propertyOwnerAPI } from "utils/api";

// Icons
import Icon from "@mui/material/Icon";

function ClosureRequests() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [requests, setRequests] = useState([]);
  const [properties, setProperties] = useState([]);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    propertyId: "",
    startDate: "",
    endDate: "",
    reason: "",
  });

  useEffect(() => {
    fetchRequests();
    fetchProperties();
  }, []);

  const fetchProperties = async () => {
    try {
      const response = await propertyOwnerAPI.getProperties();
      // Filter only active/approved properties
      const activeProperties = (response.data || response.properties || []).filter(
        (p) => p.status === "Active" || p.status === "Approved"
      );
      setProperties(activeProperties);
    } catch (error) {
      console.error("Error fetching properties:", error);
    }
  };

  const fetchRequests = async () => {
    try {
      setLoading(true);
      setMessage({ type: "", text: "" });
      const response = await propertyOwnerAPI.getClosureRequests();
      setRequests(response.requests || []);
    } catch (error) {
      console.error("Error fetching closure requests:", error);
      setMessage({
        type: "error",
        text: error.message || "Failed to load closure requests",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = () => {
    setFormData({
      propertyId: "",
      startDate: "",
      endDate: "",
      reason: "",
    });
    setMessage({ type: "", text: "" });
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    if (!submitting) {
      setDialogOpen(false);
      setFormData({
        propertyId: "",
        startDate: "",
        endDate: "",
        reason: "",
      });
      setMessage({ type: "", text: "" });
    }
  };

  const handleSubmit = async () => {
    if (!formData.propertyId || !formData.startDate || !formData.endDate) {
      setMessage({ type: "error", text: "Please fill in all required fields" });
      return;
    }

    const start = new Date(formData.startDate);
    const end = new Date(formData.endDate);

    if (end < start) {
      setMessage({ type: "error", text: "End date must be on or after start date" });
      return;
    }

    try {
      setSubmitting(true);
      setMessage({ type: "", text: "" });

      const response = await propertyOwnerAPI.createClosureRequest({
        propertyId: formData.propertyId,
        startDate: formData.startDate,
        endDate: formData.endDate,
        reason: formData.reason,
      });

      setMessage({ type: "success", text: "Closure request submitted successfully" });
      setDialogOpen(false);
      setFormData({
        propertyId: "",
        startDate: "",
        endDate: "",
        reason: "",
      });

      await fetchRequests();

      setTimeout(() => setMessage({ type: "", text: "" }), 3000);
    } catch (error) {
      console.error("Error creating closure request:", error);
      setMessage({
        type: "error",
        text: error.message || "Failed to submit closure request",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelRequest = async (requestId) => {
    if (!window.confirm("Are you sure you want to cancel this closure request?")) {
      return;
    }

    try {
      await propertyOwnerAPI.cancelClosureRequest(requestId);
      setMessage({ type: "success", text: "Closure request cancelled successfully" });
      await fetchRequests();
      setTimeout(() => setMessage({ type: "", text: "" }), 3000);
    } catch (error) {
      console.error("Error cancelling closure request:", error);
      setMessage({
        type: "error",
        text: error.message || "Failed to cancel closure request",
      });
      setTimeout(() => setMessage({ type: "", text: "" }), 5000);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Pending":
        return "warning";
      case "Approved":
        return "success";
      case "Rejected":
        return "error";
      case "Cancelled":
        return "default";
      default:
        return "default";
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox py={3} display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </MDBox>
        <PropertyOwnerFooter />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <MDBox mb={3} display="flex" justifyContent="space-between" alignItems="center">
          <MDBox>
            <MDTypography variant="h4" fontWeight="bold" mb={1}>
              Property Closure Requests
            </MDTypography>
            <MDTypography variant="body2" color="text.secondary">
              Request to close your properties for maintenance or personal use
            </MDTypography>
          </MDBox>
          <MDButton
            variant="gradient"
            color="info"
            startIcon={<Icon>add</Icon>}
            onClick={handleOpenDialog}
            sx={{ minHeight: "44px" }}
          >
            New Request
          </MDButton>
        </MDBox>

        {message.text && (
          <Alert
            severity={message.type}
            sx={{ mb: 3 }}
            onClose={() => setMessage({ type: "", text: "" })}
          >
            {message.text}
          </Alert>
        )}

        <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
          <MDBox p={3}>
            {requests.length === 0 ? (
              <MDBox textAlign="center" py={8}>
                <Icon sx={{ fontSize: "4rem", color: "text.secondary", mb: 2 }}>event_busy</Icon>
                <MDTypography variant="h6" color="text.secondary" mb={1}>
                  No Closure Requests
                </MDTypography>
                <MDTypography variant="body2" color="text.secondary" mb={3}>
                  You haven't submitted any closure requests yet.
                </MDTypography>
                <MDButton
                  variant="gradient"
                  color="info"
                  startIcon={<Icon>add</Icon>}
                  onClick={handleOpenDialog}
                  sx={{ minHeight: "44px" }}
                >
                  Create First Request
                </MDButton>
              </MDBox>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Property</TableCell>
                      <TableCell>Start Date</TableCell>
                      <TableCell>End Date</TableCell>
                      <TableCell>Days</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Reason</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {requests.map((request) => (
                      <TableRow key={request._id}>
                        <TableCell>
                          <MDTypography variant="body2" fontWeight="medium">
                            {request.property?.title?.en || request.property?.title || "N/A"}
                          </MDTypography>
                        </TableCell>
                        <TableCell>{formatDate(request.startDate)}</TableCell>
                        <TableCell>{formatDate(request.endDate)}</TableCell>
                        <TableCell>{request.numberOfDays}</TableCell>
                        <TableCell>
                          <Chip
                            label={request.status}
                            color={getStatusColor(request.status)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <MDTypography
                            variant="body2"
                            color="text.secondary"
                            sx={{ maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis" }}
                          >
                            {request.reason || "N/A"}
                          </MDTypography>
                        </TableCell>
                        <TableCell>
                          {request.status === "Pending" && (
                            <Tooltip title="Cancel Request">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleCancelRequest(request._id)}
                              >
                                <Icon fontSize="small">cancel</Icon>
                              </IconButton>
                            </Tooltip>
                          )}
                          {request.status === "Rejected" && request.adminNotes && (
                            <Tooltip title={request.adminNotes}>
                              <Icon color="error" fontSize="small">
                                info
                              </Icon>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </MDBox>
        </Card>
      </MDBox>

      {/* Create Request Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
          },
        }}
      >
        <DialogTitle
          sx={{
            background: "linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)",
            color: "white",
            py: 2.5,
            px: 3,
          }}
        >
          <MDBox display="flex" alignItems="center" gap={2}>
            <Icon sx={{ fontSize: "2rem" }}>event_busy</Icon>
            <MDTypography variant="h5" fontWeight="bold">
              Request Property Closure
            </MDTypography>
          </MDBox>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          {message.text && (
            <Alert severity={message.type} sx={{ mb: 2 }}>
              {message.text}
            </Alert>
          )}

          <MDBox>
            <MDTypography variant="body2" color="text.secondary" mb={3}>
              Request to close your property for maintenance or personal use. Each closure period
              cannot exceed the maximum days set by your package, and total closure days per year
              cannot exceed 20 days.
            </MDTypography>

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Property *</InputLabel>
                  <Select
                    value={formData.propertyId}
                    label="Property *"
                    onChange={(e) => setFormData({ ...formData, propertyId: e.target.value })}
                    sx={{ minHeight: "44px" }}
                  >
                    <MenuItem value="">
                      <em>Select a property</em>
                    </MenuItem>
                    {properties.map((property) => (
                      <MenuItem
                        key={property._id || property.id}
                        value={property._id || property.id}
                      >
                        {property.title?.en || property.title || "Property"}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="date"
                  label="Start Date *"
                  value={formData.startDate}
                  onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: new Date().toISOString().split("T")[0] }}
                  sx={{ minHeight: "44px" }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="date"
                  label="End Date *"
                  value={formData.endDate}
                  onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: formData.startDate || new Date().toISOString().split("T")[0] }}
                  sx={{ minHeight: "44px" }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Reason (Optional)"
                  value={formData.reason}
                  onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                  placeholder="Explain why you need to close the property..."
                />
              </Grid>
            </Grid>
          </MDBox>
        </DialogContent>

        <DialogActions sx={{ p: 3, bgcolor: "grey.50" }}>
          <MDButton
            onClick={handleCloseDialog}
            color="secondary"
            variant="outlined"
            disabled={submitting}
            sx={{ minHeight: "44px", px: 3 }}
          >
            Cancel
          </MDButton>
          <MDButton
            onClick={handleSubmit}
            color="info"
            variant="gradient"
            disabled={
              submitting || !formData.propertyId || !formData.startDate || !formData.endDate
            }
            startIcon={
              submitting ? <CircularProgress size={20} color="inherit" /> : <Icon>send</Icon>
            }
            sx={{ minHeight: "44px", px: 4 }}
          >
            {submitting ? "Submitting..." : "Submit Request"}
          </MDButton>
        </DialogActions>
      </Dialog>

      <PropertyOwnerFooter />
    </DashboardLayout>
  );
}

export default ClosureRequests;
